import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, LinearProgress, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
  Tabs, Tab, TextField, MenuItem,
} from '@mui/material';
import { CloudUpload, PlayArrow, Visibility } from '@mui/icons-material';
import TermHelp from '../components/TermHelp';
import IngestionFlowDiagram from '../components/IngestionFlowDiagram';
import PageHeader from '../components/PageHeader';
import { getActiveProjectId, subscribeProjectChange } from '../services/projectContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

type Connector = {
  connector_id: string; authority_code: string; source_name: string; source_type: string;
  ingestion_mode: string; expected_cadence: string; supports_run_now: boolean;
  supports_upload: boolean; active_record_count: number; accepted_extensions: string[];
  last_successful_run: Run | null;
  pending_review_count: number; checkpoint: { scope_key: string; next_offset: number; total_seen: number; last_run_id: number | null } | null;
};
type Run = {
  id: number; connector_id: string; status: string; stage: string; finished_at: string | null;
  records_discovered: number; records_created: number; records_updated: number;
  records_unchanged: number; records_rejected: number; warnings: number;
};
type ReviewRecord = {
  id: number; connector_id: string; external_source_id: string; canonical_data: Record<string, any>;
  provenance: Record<string, any>; review_reason: string | null; review_status: string;
};

const statusColor = (value: string): 'success' | 'warning' | 'error' | 'default' =>
  value === 'completed' ? 'success' : value === 'completed_with_warnings' ? 'warning' : value === 'failed' ? 'error' : 'default';

export default function DataOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'health';
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [uploadConnector, setUploadConnector] = useState<Connector | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [reviewRecords, setReviewRecords] = useState<ReviewRecord[]>([]);
  const [reviewing, setReviewing] = useState<ReviewRecord | null>(null);
  const [reviewProject, setReviewProject] = useState('');
  const [reviewType, setReviewType] = useState('');
  const [reviewReason, setReviewReason] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewProjects, setReviewProjects] = useState<any[]>([]);
  const [reviewTypes, setReviewTypes] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<number | null>(getActiveProjectId());
  const navigate = useNavigate();
  const setView = (_: React.SyntheticEvent, next: string) => setSearchParams({ view: next });
  const scopedQuery = (path: string) => {
    const separator = path.includes('?') ? '&' : '?';
    return projectId ? `${API_BASE}${path}${separator}project_id=${projectId}` : `${API_BASE}${path}`;
  };

  const load = async () => {
    const [connectorRes, runRes] = await Promise.all([
      fetch(scopedQuery('/ingestion/connectors')), fetch(`${API_BASE}/ingestion/runs`),
    ]);
    if (!connectorRes.ok || !runRes.ok) throw new Error('Could not load ingestion operations');
    setConnectors(await connectorRes.json());
    setRuns(await runRes.json());
  };

  useEffect(() => { load().catch((e) => setError(e.message)); }, [projectId]);
  useEffect(() => subscribeProjectChange(project => setProjectId(project.id)), []);
  useEffect(() => {
    if (view !== 'review-queue') return;
    fetch(scopedQuery('/ingestion/review?status=pending&limit=100'))
      .then((response) => response.ok ? response.json() : [])
      .then((records) => setReviewRecords(records))
      .catch(() => setReviewRecords([]));
  }, [view, projectId]);

  useEffect(() => {
    if (view !== 'review-queue') return;
    Promise.all([fetch(`${API_BASE}/consents/projects`), fetch(`${API_BASE}/consents/consent-types`)]).then(async ([projects, types]) => {
      if (projects.ok) setReviewProjects(await projects.json());
      if (types.ok) setReviewTypes(await types.json());
    }).catch(() => undefined);
  }, [view]);

  const openReview = (record: ReviewRecord) => {
    const data = record.canonical_data || {};
    setReviewing(record);
    setReviewProject(data.project || '');
    setReviewType(data.consent_type || record.provenance?.review_recommendation?.suggested_consent_types?.[0] || '');
    setReviewReason('');
  };

  const submitReview = async (decision: 'approve' | 'reject' | 'defer') => {
    if (!reviewing) return;
    if (decision === 'approve' && (!reviewProject || !reviewType)) { setError('Choose a project and consent type before approving this record.'); return; }
    setReviewBusy(true); setError('');
    try {
      const response = await fetch(`${API_BASE}/ingestion/review/${reviewing.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, project: reviewProject || undefined, consent_type: reviewType || undefined, reason: reviewReason.trim() || undefined }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Review decision could not be saved.');
      setReviewing(null);
      setReviewRecords(items => items.filter(item => item.id !== reviewing.id));
      await load();
    } catch (e: any) { setError(e.message); } finally { setReviewBusy(false); }
  };

  const runNow = async (connector: Connector) => {
    setBusy(connector.connector_id); setError('');
    try {
      const response = await fetch(`${API_BASE}/ingestion/connectors/${connector.connector_id}/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.error_summary || 'Ingestion failed');
      await load(); navigate(`/data-operations/runs/${data.id}`);
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  const inspectUpload = async () => {
    if (!uploadConnector || !file) return;
    setBusy(uploadConnector.connector_id); setError('');
    const body = new FormData(); body.append('file', file);
    try {
      const response = await fetch(`${API_BASE}/ingestion/connectors/${uploadConnector.connector_id}/preview`, {
        method: 'POST', body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data);
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(uploadConnector!.connector_id);
    try {
      const response = await fetch(`${API_BASE}/ingestion/runs/${preview.run.id}/confirm`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.error_summary || 'Import failed');
      setUploadConnector(null); setPreview(null); setFile(null);
      await load(); navigate(`/data-operations/runs/${data.id}`);
    } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  return (
    <Box>
      <PageHeader title="Data Sources" subtitle="Monitor where regulatory information comes from, when it was last checked, and whether anything needs attention." />
      <Button component={RouterLink} to="/help?article=importing-register" size="small" variant="text" sx={{ mb: 1.5, textTransform: 'none' }}>How imports and review work</Button>
      <Paper sx={{ mb: 3, px: 1 }}><Tabs value={view} onChange={setView} variant="scrollable" aria-label="Data source views"><Tab value="health" label="Source health" /><Tab value="review-queue" label="Review queue" /><Tab value="history" label="Run history" /><Tab value="connectors" label="Connectors" /></Tabs></Paper>
      {view === 'review-queue' && <Paper sx={{ p: 2, mb: 3 }}><Typography variant="h5" gutterBottom>Imported records awaiting review</Typography><Typography color="text.secondary" sx={{ mb: 2 }}>Select a record to inspect its source evidence and make a review decision.</Typography><TableContainer><Table size="small"><TableHead><TableRow><TableCell>Record · review</TableCell><TableCell>Source</TableCell><TableCell>Authority</TableCell><TableCell>Review reason</TableCell></TableRow></TableHead><TableBody>{reviewRecords.map((record) => { const data = record.canonical_data || {}; const source = record.provenance?.source_name || record.connector_id; const reason = record.review_reason || record.provenance?.review_recommendation?.reason || 'Mapping decision required'; return <TableRow key={record.id}><TableCell><Button variant="text" onClick={() => openReview(record)} sx={{ p: 0, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontWeight: 600, whiteSpace: 'normal' }}>{data.title || data.reference || record.external_source_id}</Button><Typography variant="caption" display="block" color="text.secondary">{data.reference || record.external_source_id}</Typography></TableCell><TableCell>{source}</TableCell><TableCell>{record.provenance?.authority_code || record.provenance?.authority || '—'}</TableCell><TableCell>{reason.replace(/LAP review/gi, 'Assertion review')}</TableCell></TableRow>; })}{!reviewRecords.length && <TableRow><TableCell colSpan={4}><Typography color="text.secondary" sx={{ py: 2 }}>No imported records are currently awaiting review.</Typography></TableCell></TableRow>}</TableBody></Table></TableContainer></Paper>}
      <Dialog open={Boolean(reviewing)} onClose={() => !reviewBusy && setReviewing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Review imported record</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mt: 1 }}>{reviewing?.canonical_data?.title || reviewing?.external_source_id}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>This record remains outside the consent register until you make a decision.</Typography>
          {reviewing && <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Stack spacing={0.75}>
              <Typography variant="body2"><strong>Reference:</strong> {reviewing.canonical_data?.reference || reviewing.external_source_id || '—'}</Typography>
              <Typography variant="body2"><strong>Authority:</strong> {reviewing.provenance?.authority_code || reviewing.provenance?.authority || '—'}</Typography>
              {reviewing.provenance?.document_stage && <Typography variant="body2"><strong>Document stage:</strong> {reviewing.provenance.document_stage}</Typography>}
              {reviewing.canonical_data?.description && <Typography variant="body2"><strong>Description:</strong> {reviewing.canonical_data.description}</Typography>}
              {(reviewing.canonical_data?.source_url || reviewing.provenance?.source_url) && <Button component="a" href={reviewing.canonical_data?.source_url || reviewing.provenance?.source_url} target="_blank" rel="noreferrer" size="small" sx={{ alignSelf: 'flex-start', px: 0 }}>Open source document</Button>}
            </Stack>
          </Paper>}
          <Stack spacing={2}>
            <TextField select label="Assertion project" value={reviewProject} onChange={event => setReviewProject(event.target.value)} fullWidth>
              <MenuItem value="">Select project</MenuItem>{reviewProjects.map(project => <MenuItem key={project.id || project.name} value={project.name}>{project.name}</MenuItem>)}
            </TextField>
            <TextField select label="Consent type" value={reviewType} onChange={event => setReviewType(event.target.value)} fullWidth>
              <MenuItem value="">Select consent type</MenuItem>{reviewTypes.map(type => <MenuItem key={type.id || type.code} value={type.code}>{type.code} · {type.name}</MenuItem>)}
            </TextField>
            <TextField label="Reviewer rationale (optional for approve/defer)" value={reviewReason} onChange={event => setReviewReason(event.target.value)} multiline minRows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewing(null)} disabled={reviewBusy}>Cancel</Button>
          <Button color="inherit" onClick={() => submitReview('defer')} disabled={reviewBusy}>Defer</Button>
          <Button color="error" onClick={() => submitReview('reject')} disabled={reviewBusy}>Reject</Button>
          <Button variant="contained" onClick={() => submitReview('approve')} disabled={reviewBusy}>Approve into register</Button>
        </DialogActions>
      </Dialog>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {busy && <LinearProgress sx={{ mb: 2 }} />}

      <Alert severity="info" sx={{ mb: 3 }}>
        Sources are checked, validated and retained with evidence before records enter the consent register. Portal-assisted sources are working integrations, even when a file upload is part of the process.
      </Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}><Typography variant="h5">{connectors.filter(c => c.last_successful_run).length}</Typography><Typography variant="caption" color="text.secondary">Sources checked successfully</Typography></Paper>
        <Paper sx={{ p: 2, flex: 1 }}><Typography variant="h5">{connectors.reduce((sum, c) => sum + c.pending_review_count, 0)}</Typography><Typography variant="caption" color="text.secondary">Records awaiting review</Typography></Paper>
        <Paper sx={{ p: 2, flex: 1 }}><Typography variant="h5">{connectors.filter(c => !c.last_successful_run).length}</Typography><Typography variant="caption" color="text.secondary">Sources needing a first check</Typography></Paper>
      </Stack>

      {view === 'health' && <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>How a source becomes usable</Typography>
        <IngestionFlowDiagram />
      </Paper>}

      {(view === 'health' || view === 'connectors') && <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom><TermHelp term="Source health" explanation="How recently each authority source was checked and whether its records are ready for use." /></Typography>
        <TableContainer sx={{ overflowX: 'auto', '& table': { minWidth: 760 } }}>
          <Table size="small">
            <TableHead><TableRow>
            <TableCell>Source</TableCell><TableCell>How it is checked</TableCell>
              <TableCell>Cadence</TableCell><TableCell>Last success</TableCell>
              <TableCell>Active records</TableCell><TableCell><TermHelp term="Review / checkpoint" explanation="Records held for a mapping decision, and the saved position for the next bounded source run." /></TableCell><TableCell align="right">Action</TableCell>
            </TableRow></TableHead>
            <TableBody>{connectors.map((connector) => (
              <TableRow key={connector.connector_id}>
                <TableCell>
                  <Typography fontWeight={600} variant="body2">{connector.source_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{connector.authority_code} · {connector.source_type}</Typography>
                </TableCell>
                <TableCell><Chip size="small" label={
                  connector.ingestion_mode === 'automated' ? 'Automated' :
                  connector.ingestion_mode === 'semi_automated' ? 'Scheduled download' : 'Portal-assisted'
                } color={connector.ingestion_mode === 'manual' ? 'warning' : 'primary'} /></TableCell>
                <TableCell>{connector.expected_cadence}</TableCell>
                <TableCell>{connector.last_successful_run?.finished_at
                  ? new Date(connector.last_successful_run.finished_at).toLocaleString() : 'Never'}</TableCell>
                <TableCell>{connector.active_record_count}</TableCell>
                <TableCell><Typography variant="caption" display="block">{connector.pending_review_count} pending review</Typography>
                  {connector.checkpoint && <Typography variant="caption" color="text.secondary">{connector.checkpoint.scope_key} · next {connector.checkpoint.next_offset}</Typography>}</TableCell>
                <TableCell align="right">
                  {connector.supports_run_now && <Button size="small" startIcon={<PlayArrow />} disabled={!!busy} onClick={() => runNow(connector)}>Run now</Button>}
                  {connector.supports_upload && <Button size="small" startIcon={<CloudUpload />} disabled={!!busy} onClick={() => setUploadConnector(connector)}>Upload data</Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </TableContainer>
      </Paper>}

      {(view === 'history' || view === 'health') && <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Run history</Typography>
        <TableContainer sx={{ overflowX: 'auto', '& table': { minWidth: 680 } }}><Table size="small">
          <TableHead><TableRow><TableCell>Run</TableCell><TableCell>Connector</TableCell><TableCell>Status</TableCell>
            <TableCell>Discovered</TableCell><TableCell>Created</TableCell><TableCell>Updated</TableCell>
            <TableCell>Unchanged</TableCell><TableCell>Rejected</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>{runs.map((run) => <TableRow key={run.id}>
            <TableCell>#{run.id}</TableCell><TableCell>{run.connector_id}</TableCell>
            <TableCell><Chip size="small" color={statusColor(run.status)} label={run.status.replace(/_/g, ' ')} /></TableCell>
            <TableCell>{run.records_discovered}</TableCell><TableCell>{run.records_created}</TableCell>
            <TableCell>{run.records_updated}</TableCell><TableCell>{run.records_unchanged}</TableCell>
            <TableCell>{run.records_rejected}</TableCell>
            <TableCell><Button component={RouterLink} to={`/data-operations/runs/${run.id}`} size="small" startIcon={<Visibility />}>View</Button></TableCell>
          </TableRow>)}</TableBody></Table>
        </TableContainer>
      </Paper>}

      <Dialog open={!!uploadConnector} onClose={() => !busy && setUploadConnector(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Portal-assisted import · {uploadConnector?.authority_code}</DialogTitle>
        <DialogContent dividers>
          {!preview ? <Stack spacing={2}>
            <Alert severity="info">The file is retained as an immutable source artifact. No consent records are written until you confirm the preview.</Alert>
            <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
              {file?.name || `Choose ${uploadConnector?.accepted_extensions.join(' or ')}`}
              <input hidden type="file" accept={uploadConnector?.accepted_extensions.join(',')} onChange={(event) => setFile(event.target.files?.[0] || null)} />
            </Button>
          </Stack> : <Stack spacing={2}>
            <Alert severity={preview.run.records_rejected ? 'warning' : 'success'}>
              {preview.run.records_accepted} accepted, {preview.run.records_rejected} rejected, {preview.run.warnings} warnings. Review before confirmation.
            </Alert>
            <Typography variant="subtitle2">Field mappings</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {Object.entries(preview.field_mapping).map(([from, to]) => `${from} → ${to}`).join(' · ')}
            </Typography>
            <Divider />
            <TableContainer sx={{ maxHeight: 360, overflowX: 'auto', '& table': { minWidth: 680 } }}><Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell>Result</TableCell><TableCell>Reference</TableCell><TableCell>Title</TableCell><TableCell>Type</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>{preview.preview.map((record: any) => <TableRow key={record.row_number}>
                <TableCell><Chip size="small" color={record.accepted ? 'success' : 'error'} label={record.accepted ? 'Accepted' : 'Rejected'} /></TableCell>
                <TableCell>{record.reference || '—'}</TableCell><TableCell>{record.title || '—'}</TableCell>
                <TableCell>{record.consent_type || '—'}</TableCell><TableCell>{record.status || '—'}</TableCell>
              </TableRow>)}</TableBody></Table>
            </TableContainer>
            {!!preview.run.issues?.length && <Alert severity="warning">
              {preview.run.issues.map((issue: any) => `Row ${issue.row_number}: ${issue.message}`).join(' · ')}
            </Alert>}
          </Stack>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadConnector(null); setPreview(null); setFile(null); }}>Cancel</Button>
          {!preview ? <Button variant="contained" disabled={!file || !!busy} onClick={inspectUpload}>Validate and preview</Button>
            : <Button variant="contained" disabled={!!busy} onClick={confirm}>Confirm import</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
